// @ts-strict-ignore
import * as React from 'react';
import Badge from 'antd/lib/badge';
import Tooltip from 'antd/lib/tooltip';
import { ProfileOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';

/** Default Ant Design tooltip overlay uses a dark background; theme text tokens stay dark and are unreadable. */
const tooltipOnDark = {
    title: 'rgba(255, 255, 255, 0.95)',
    body: 'rgba(255, 255, 255, 0.88)',
} as const;

export function countNonEmptyWorkflowVariables(workflowVariables?: Record<string, string> | null): number {
    if (!workflowVariables) {
        return 0;
    }
    return Object.entries(workflowVariables).filter(([, v]) => v != null && String(v).trim() !== '').length;
}

export function workflowVariablesTooltipContent(workflowVariables?: Record<string, string> | null): React.ReactNode {
    const entries = Object.entries(workflowVariables ?? {}).filter(([, v]) => v != null && String(v).trim() !== '');
    return (
        <div style={{ maxWidth: 380, color: tooltipOnDark.body }}>
            <div style={{ marginBottom: 8, fontWeight: 600, color: tooltipOnDark.title }}>Workflow arguments</div>
            {entries.map(([k, v]) => (
                <div key={k} style={{ fontSize: 12, marginBottom: 4, wordBreak: 'break-word' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: tooltipOnDark.body }}>{k}</span>
                    <span style={{ color: tooltipOnDark.body }}>: </span>
                    <span style={{ color: tooltipOnDark.body }}>{v}</span>
                </div>
            ))}
        </div>
    );
}

/** Header values are secret and never returned to the client, so they are always shown redacted. */
export const REDACTED_HEADER_VALUE = '••••••••••••';

export function customCITriggerConfiguredCount(triggerBodyTemplate?: string, triggerHeaderKeys?: string[]): number {
    const hasBody = (triggerBodyTemplate ?? '').trim() !== '';
    return (hasBody ? 1 : 0) + (triggerHeaderKeys?.length ?? 0);
}

export function customCITriggerTooltipContent(
    triggerBodyTemplate?: string,
    triggerHeaderKeys?: string[],
): React.ReactNode {
    const body = (triggerBodyTemplate ?? '').trim();
    const headerKeys = triggerHeaderKeys ?? [];
    return (
        <div style={{ minWidth: 300, color: tooltipOnDark.body }}>
            {headerKeys.length > 0 && (
                <div style={{ marginBottom: body !== '' ? 14 : 0 }}>
                    <div style={{ marginBottom: 6, fontWeight: 600, color: tooltipOnDark.title }}>Custom headers</div>
                    {headerKeys.map((key) => (
                        <div key={key} style={{ fontSize: 12, marginBottom: 4, wordBreak: 'break-word' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: tooltipOnDark.body }}>
                                {key}
                            </span>
                            <span style={{ color: tooltipOnDark.body }}>: {REDACTED_HEADER_VALUE}</span>
                        </div>
                    ))}
                </div>
            )}
            {body !== '' && (
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 600, color: tooltipOnDark.title }}>
                        Custom request body
                    </div>
                    <pre
                        style={{
                            margin: 0,
                            fontFamily: 'monospace',
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: tooltipOnDark.body,
                        }}
                    >
                        {body}
                    </pre>
                </div>
            )}
        </div>
    );
}

export interface CustomCITriggerIndicatorProps {
    triggerBodyTemplate?: string;
    triggerHeaderKeys?: string[];
}

/** Custom CI analogue of the workflow-variables indicator: surfaces the configured trigger body and header keys. */
export const CustomCITriggerIndicator: React.FC<CustomCITriggerIndicatorProps> = ({
    triggerBodyTemplate,
    triggerHeaderKeys,
}) => {
    const count = customCITriggerConfiguredCount(triggerBodyTemplate, triggerHeaderKeys);
    if (count === 0) {
        return null;
    }

    return (
        <Tooltip
            title={customCITriggerTooltipContent(triggerBodyTemplate, triggerHeaderKeys)}
            overlayStyle={{ maxWidth: 480 }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', paddingRight: 6 }}>
                <Badge
                    count={count}
                    size="small"
                    offset={[2, -2]}
                    styles={{
                        indicator: {
                            background: antGrey.grey6,
                            color: runwayColors.white,
                            boxShadow: 'none',
                            fontSize: 11,
                            minWidth: 16,
                            height: 16,
                            padding: '0 4px',
                            borderRadius: 8,
                            lineHeight: '16px',
                            textAlign: 'center',
                        },
                    }}
                >
                    <ProfileOutlined style={{ color: antGrey.grey7, fontSize: 17, position: 'relative', top: 1 }} />
                </Badge>
            </span>
        </Tooltip>
    );
};

export interface WorkflowVariablesIndicatorProps {
    workflowVariables?: Record<string, string>;
}

/** Shows workflow variable count with a tooltip listing configured (non-empty) values. Renders nothing if none are set. */
export const WorkflowVariablesIndicator: React.FC<WorkflowVariablesIndicatorProps> = ({ workflowVariables }) => {
    const count = countNonEmptyWorkflowVariables(workflowVariables);
    if (count === 0) {
        return null;
    }

    return (
        <Tooltip title={workflowVariablesTooltipContent(workflowVariables)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', paddingRight: 6 }}>
                <Badge
                    count={count}
                    size="small"
                    offset={[2, -2]}
                    styles={{
                        indicator: {
                            background: antGrey.grey6,
                            color: runwayColors.white,
                            boxShadow: 'none',
                            fontSize: 11,
                            minWidth: 16,
                            height: 16,
                            padding: '0 4px',
                            borderRadius: 8,
                            // Do not use display:flex here — ScrollNumber stacks digits with position:absolute
                            // and percentage top; flex breaks that and shoves the glyph to the corner.
                            lineHeight: '16px',
                            textAlign: 'center',
                        },
                    }}
                >
                    <ProfileOutlined style={{ color: antGrey.grey7, fontSize: 17, position: 'relative', top: 1 }} />
                </Badge>
            </span>
        </Tooltip>
    );
};
